/**
 * useCopyContent — TanStack Query mutation for copying day content
 * between audiences.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { contentKeys } from '@/hooks/useContent';
import { copyDayContent } from '@/services/copyService';
import type { Audience } from '@/types/auth.types';
import type { ContentFieldKey, CopyConflictStrategy } from '@/types/content.types';

export function useCopyContent() {
  const queryClient = useQueryClient();
  const { cmsUser } = useAuth();

  return useMutation({
    mutationFn: async ({
      sourceAudience,
      targetAudience,
      dayNumber,
      fields,
      conflictStrategy,
    }: {
      sourceAudience:   Audience;
      targetAudience:   Audience;
      dayNumber:        number;
      fields:           ContentFieldKey[];
      conflictStrategy: CopyConflictStrategy;
    }) => {
      if (!cmsUser) throw new Error('Not authenticated');
      const userRef = {
        uid:         cmsUser.uid,
        displayName: cmsUser.displayName,
        email:       cmsUser.email,
      };
      return copyDayContent(
        sourceAudience,
        targetAudience,
        dayNumber,
        fields,
        conflictStrategy,
        userRef,
      );
    },

    onSuccess: (_, { targetAudience, dayNumber }) => {
      // Invalidate the target day + its audience status map
      queryClient.invalidateQueries({
        queryKey: contentKeys.day(targetAudience, dayNumber),
      });
      queryClient.invalidateQueries({
        queryKey: contentKeys.audienceStatuses(targetAudience),
      });
    },
  });
}
